import React, { ReactNode } from "react";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ediphiPalette } from "../../styles/colors";
import { PrimaryButton } from "../buttons/PrimaryButton.styled";
import { SecondaryButton } from "../buttons/SecondaryButton.styled";
import { DestructiveButton } from "../buttons/DestructiveButton.styled";
import {
  DialogActions,
  DialogContent,
  Typography,
  Dialog as MuiDialog,
  DialogTitle,
  Alert,
  AlertColor,
  Box,
} from "@mui/material";
import { AnimatedCheckmark } from "../icons/AnimatedCheckmark";
import { CANCEL_ACTION, CONFIRM_ACTION, DELETE_ACTION } from "../../constants";

export interface DialogProps {
  title: ReactNode;
  description: ReactNode;
  alert?: {
    severity?: AlertColor;
    content: ReactNode;
  };
  submit?: {
    label?: string;
    action: () => void;
    /** Warns in the title, reddens the action, and labels it DELETE_ACTION unless `label` says otherwise. */
    destructive?: boolean;
  };
  cancel?: {
    label?: string;
    action: () => void;
  };
  /** Invoked when the dialog is dismissed without submit (backdrop, Escape, or cancel). */
  onClose?: () => void;
  preventClose?: boolean;
}

export const Dialog = ({ title, description, alert, submit, cancel, onClose, preventClose }: DialogProps) => {
  const SubmitButton = submit?.destructive ? DestructiveButton : PrimaryButton;
  const submitLabel = submit?.label ?? (submit?.destructive ? DELETE_ACTION : CONFIRM_ACTION);

  return (
    <MuiDialog onClose={preventClose ? undefined : onClose} open maxWidth="sm" fullWidth sx={{ m: 2 }}>
      <DialogTitle
        sx={{
          color: ediphiPalette.black950,
          fontSize: "16px",
          fontWeight: 700,
          lineHeight: "24px",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {submit?.destructive && (
          <ReportProblemIcon sx={{ color: ediphiPalette.mainYellow, width: "24px", height: "24px" }} />
        )}
        {title}
      </DialogTitle>
      <DialogContent sx={cancel || submit ? { paddingBottom: 2 } : undefined}>
        {description}
        {alert && (
          <Alert
            severity={alert?.severity || "info"}
            // A warning Alert defaults to the same triangle the destructive title already shows;
            // a circle keeps the two marks distinguishable.
            iconMapping={{ warning: <ErrorOutlineIcon fontSize="inherit" /> }}
            sx={{
              mt: 2,
              // Alert already sizes its text from the theme; nested nodes inherit rather than
              // re-declare, so wrapped content can't come out larger than a plain string.
              "& .MuiTypography-root": { fontSize: "inherit", mb: 0 },
              "& ul": { m: 0, pl: 2.5 },
            }}
          >
            {alert.content}
          </Alert>
        )}
      </DialogContent>
      {(cancel || submit) && (
        <DialogActions sx={{ padding: "0 24px 24px 24px" }}>
          {cancel && (
            <SecondaryButton size="large" autoFocus disableFocusRipple onClick={cancel.action}>
              {cancel?.label || CANCEL_ACTION}
            </SecondaryButton>
          )}
          {submit && (
            <SubmitButton size="large" disableFocusRipple onClick={submit.action}>
              {submitLabel}
            </SubmitButton>
          )}
        </DialogActions>
      )}
    </MuiDialog>
  );
};

export const DialogDescription = (description: string) => (
  <Typography variant="body1" gutterBottom>
    {description}
  </Typography>
);

/** A dialog title that opens with the success animation — for a dialog reporting a completed write. */
export const DialogSuccessTitle = (title: string) => (
  <>
    <Box component="span" sx={{ display: "inline-flex", "& .checkmark": { marginTop: 0, width: 28, height: 28 } }}>
      <AnimatedCheckmark />
    </Box>
    {title}
  </>
);

export const DialogDescriptionWithAlert = (
  description: string,
  alert: string,
  alertSeverity: AlertColor = "warning"
) => (
  <>
    <Alert severity={alertSeverity} sx={{ mb: 2 }}>
      {alert}
    </Alert>
    <Typography variant="body1">{description}</Typography>
  </>
);

export const DialogDescriptionWithAlertList = (description: string, list: string[]) => (
  <>
    <Typography variant="body2" fontWeight="bold">
      {description}
    </Typography>
    <ul>
      {list.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </>
);

export const DialogDescriptionWithCustomIcon = (description: ReactNode, icon: () => React.JSX.Element) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
    <Typography>{description}</Typography>
    {icon()}
  </Box>
);

export default Dialog;
